/**
 * Hooks for the StockMovementWizard (Receive Stock / Issue Stock).
 *
 * The wizard is an inventory-module entry point that ultimately creates
 * documents in two other modules (procurement: goods_receipts, sales:
 * sales_order_shipments). To respect the frontend module-boundary rule,
 * the inventory module talks to those endpoints through ``axiosInstance``
 * directly with locally-defined request/response shapes — it does not import
 * procurement/ or sales/ TypeScript modules.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../shared/api/axiosInstance';
import { stockMovementWizardApi } from '../services/inventoryApi';
import { stockLevelKeys, stockMovementKeys } from './useStock';
import { inventoryDashboardKeys } from './useDashboard';

// ─── Request / response shapes for the cross-module endpoints ──────────────
//
// Kept narrow to what the wizard actually sends. The authoritative shapes
// live in modules/procurement/api/routes.py and modules/sales/api/shipment_routes.py.

export interface MatchedReceiptItem {
  po_item_id: string;
  quantity_received: number;
  unit_cost?: number;
  /** Putaway bin within the receiving warehouse; null = loose (no bin). */
  location_id?: string | null;
}

export interface MatchedReceiptCreate {
  purchase_order_id: string;
  warehouse_id?: string;
  notes?: string;
  items: MatchedReceiptItem[];
}

export interface UnmatchedReceiptItem {
  product_id: string;
  variant_id?: string | null;
  quantity_received: number;
  unit_cost?: number;
  lot_id?: string | null;
  lot_code?: string | null;
  lot_expiry_date?: string | null;
  location_id?: string | null;
  description?: string | null;
}

export interface UnmatchedReceiptCreate {
  warehouse_id: string;
  vendor_id: string;
  company_id: string;
  reason_code: string;
  notes?: string | null;
  received_date?: string | null;
  items: UnmatchedReceiptItem[];
}

export interface MatchedShipmentLine {
  sales_order_item_id: string;
  quantity: number;
  /** Bin the stock is picked from; null = loose (no bin). */
  location_id?: string | null;
}

export interface MatchedShipmentCreate {
  order_id: string;
  warehouse_id?: string | null;
  lines: MatchedShipmentLine[];
}

export interface UnmatchedShipmentItem {
  product_id: string;
  variant_id?: string | null;
  quantity: number;
  unit_price?: number;
  unit_cost?: number;
  location_id?: string | null;
  description?: string | null;
}

export interface UnmatchedShipmentCreate {
  warehouse_id: string;
  customer_id: string;
  company_id: string;
  reason_code: string;
  notes?: string | null;
  items: UnmatchedShipmentItem[];
}

/** A goods receipt or shipment summary — what the wizard needs after create. */
export interface DocumentRef {
  id: string;
  /** Shipment endpoints also surface their parent SO id for navigation. */
  sales_order_id?: string;
}

const DRAFT_RECEIPT_ID = 'draftReceiptId';

export function getDraftReceiptId(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const value = (error as Record<string, unknown>)[DRAFT_RECEIPT_ID];
  return typeof value === 'string' ? value : null;
}

// ─── Query keys ────────────────────────────────────────────────────────────

export const stockMovementWizardKeys = {
  all: ['stock-movement-wizard'] as const,
  openPOs: (vendorId: string) =>
    [...stockMovementWizardKeys.all, 'open-pos', vendorId] as const,
  openSOs: (customerId: string) =>
    [...stockMovementWizardKeys.all, 'open-sos', customerId] as const
};

// ─── Open document lookups ─────────────────────────────────────────────────

export function useOpenPurchaseOrdersForVendor(vendorId: string) {
  return useQuery({
    queryKey: stockMovementWizardKeys.openPOs(vendorId),
    queryFn: () => stockMovementWizardApi.listOpenPurchaseOrders(vendorId),
    enabled: !!vendorId,
    staleTime: 30 * 1000
  });
}

export function useOpenSalesOrdersForCustomer(customerId: string) {
  return useQuery({
    queryKey: stockMovementWizardKeys.openSOs(customerId),
    queryFn: () => stockMovementWizardApi.listOpenSalesOrders(customerId),
    enabled: !!customerId,
    staleTime: 30 * 1000
  });
}

// ─── Matched-flow create (inbound) ─────────────────────────────────────────
//
// Receipt against an existing PO: create draft → confirm. The wizard calls
// both endpoints sequentially in one mutation.

export function useCreateMatchedReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: MatchedReceiptCreate): Promise<DocumentRef> => {
      const createRes = await axiosInstance.post(
        '/api/v1/procurement/receipts',
        data
      );
      const draft = createRes.data.data as DocumentRef;
      try {
        await axiosInstance.post(
          `/api/v1/procurement/receipts/${draft.id}/receive`
        );
      } catch (error) {
        if (typeof error === 'object' && error !== null) {
          Object.assign(error, { [DRAFT_RECEIPT_ID]: draft.id });
        }
        throw error;
      }
      return draft;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['procurement-goods-receipts']
      });
      queryClient.invalidateQueries({
        queryKey: ['procurement-purchase-orders']
      });
      queryClient.invalidateQueries({ queryKey: stockLevelKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryDashboardKeys.all });
    }
  });
}

export function useConfirmMatchedReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (receiptId: string): Promise<DocumentRef> => {
      await axiosInstance.post(
        `/api/v1/procurement/receipts/${receiptId}/receive`,
        undefined,
        { params: { confirm_over_receipt: true } }
      );
      return { id: receiptId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['procurement-goods-receipts']
      });
      queryClient.invalidateQueries({
        queryKey: ['procurement-purchase-orders']
      });
      queryClient.invalidateQueries({ queryKey: stockLevelKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryDashboardKeys.all });
    }
  });
}

// ─── Unmatched-flow create (inbound) ───────────────────────────────────────

export function useCreateUnmatchedReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UnmatchedReceiptCreate): Promise<DocumentRef> => {
      const res = await axiosInstance.post(
        '/api/v1/procurement/receipts/unmatched',
        data
      );
      return res.data.data as DocumentRef;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['procurement-goods-receipts']
      });
      queryClient.invalidateQueries({
        queryKey: ['procurement-purchase-orders']
      });
      queryClient.invalidateQueries({ queryKey: stockLevelKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryDashboardKeys.all });
    }
  });
}

// ─── Matched-flow create (outbound) ────────────────────────────────────────
//
// Shipment against an existing SO: create pending → ship.

export function useCreateMatchedShipment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: MatchedShipmentCreate): Promise<DocumentRef> => {
      const createRes = await axiosInstance.post(
        '/api/v1/sales/shipments/',
        data
      );
      const pending = createRes.data.data as DocumentRef;
      await axiosInstance.post(
        `/api/v1/sales/shipments/${pending.id}/ship`,
        {}
      );
      return pending;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockLevelKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryDashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
    }
  });
}

// ─── Unmatched-flow create (outbound) ──────────────────────────────────────

export function useCreateUnmatchedShipment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UnmatchedShipmentCreate): Promise<DocumentRef> => {
      const res = await axiosInstance.post(
        '/api/v1/sales/shipments/unmatched',
        data
      );
      return res.data.data as DocumentRef;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockLevelKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryDashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
    }
  });
}

// ─── Reconciliation (link unmatched doc to a real PO/SO) ───────────────────

export function useLinkReceiptToPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      receiptId,
      purchaseOrderId
    }: {
      receiptId: string;
      purchaseOrderId: string;
    }): Promise<DocumentRef> => {
      const res = await axiosInstance.post(
        `/api/v1/procurement/receipts/${receiptId}/link`,
        { purchase_order_id: purchaseOrderId }
      );
      return res.data.data as DocumentRef;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['procurement-goods-receipts']
      });
      queryClient.invalidateQueries({
        queryKey: ['procurement-purchase-orders']
      });
      queryClient.invalidateQueries({ queryKey: inventoryDashboardKeys.all });
    }
  });
}

export function useLinkShipmentToSalesOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      shipmentId,
      salesOrderId
    }: {
      shipmentId: string;
      salesOrderId: string;
    }): Promise<DocumentRef> => {
      const res = await axiosInstance.post(
        `/api/v1/sales/shipments/${shipmentId}/link`,
        { sales_order_id: salesOrderId }
      );
      return res.data.data as DocumentRef;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: inventoryDashboardKeys.all });
    }
  });
}
